/**
 * useCesiumPlot.ts — Master hook connecting WebSocket telemetry to Cesium visualization
 *
 * Responsibilities:
 *  1. Build elementMap + nodeIdToElementId from deploymentData
 *  2. Listen to RepElementFixedState → plot entities, orbit, FOV, FOR, axes, trails
 *  3. Listen to RepGetLinkStats → store link stats, update comm lines
 *  4. Store ECEF history for time scrubbing
 *  5. Track selected element info
 *  6. Cleanup on unmount
 */

import { useCallback, useEffect, useRef } from 'react';
import useGenericSimulator from './useGenericSimulator';
import {
    appendEcefHistory,
    buildElementMapFromDeployment,
    cleanupCesiumPlot,
    computeAltitudeKm,
    computeLVLHEulerAngles,
    computeSpeedMs,
    handleRepElementFixedState,
    inferElementTypeFromName,
    isFeatureVisible,
    isViewerReady,
    parseLinkStatsFromPayload,
    parsePositionFromPayload,
    throttledRequestRender,
    updateGeometricCommLines,
    updatePeerCommLines,
} from '../helpers/cesiumPlotHelpers';
import { ECEFState, RepElementFixedStatePayload, WSMessage } from '../types';

const useCesiumPlot = () => {
    const {
        viewer,
        socket,
        deploymentData,
        elementMap,
        setElementMap,
        visualizationSettings,
        setSelectedElement,
        nodeIdToElementId,
        setNodeIdToElementId,
        linkStatsMap,
        setLinkStatsMap,
    } = useGenericSimulator();

    const elementMapRef = useRef(elementMap);
    elementMapRef.current = elementMap;

    const visSettingsRef = useRef(visualizationSettings);
    visSettingsRef.current = visualizationSettings;

    const linkStatsRef = useRef(linkStatsMap);
    linkStatsRef.current = linkStatsMap;

    const nodeMapRef = useRef(nodeIdToElementId);
    nodeMapRef.current = nodeIdToElementId;

    // ─── Step 1: Build elementMap when deploymentData arrives ────────
    useEffect(() => {
        if (!deploymentData) return;
        const map = buildElementMapFromDeployment(deploymentData);
        setElementMap(map);
        elementMapRef.current = map;
    }, [deploymentData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Step 2: WS message handler ─────────────────────────────────
    const handleWSMessage = useCallback((event: MessageEvent) => {
        if (!isViewerReady(viewer)) return;

        try {
            const msg: WSMessage = JSON.parse(event.data);

            // Guard against malformed messages missing required fields
            if (!msg || !msg.commandName || !msg.elementId) return;

            if (msg.commandName === 'RepElementFixedState') {
                const payload = msg.payload as RepElementFixedStatePayload;
                if (!payload) return;

                // Auto-register unknown elements
                if (!elementMapRef.current[msg.elementId]) {
                    const isFixed = payload.RepElementFixedState__vel_x === 0
                        && payload.RepElementFixedState__vel_y === 0
                        && payload.RepElementFixedState__vel_z === 0;

                    // Infer type from name (e.g., "Missile_1" → missile)
                    const nameInferred = inferElementTypeFromName(msg.elementName);
                    const autoType = isFixed
                        ? 'groundstation'
                        : nameInferred !== 'unknown'
                            ? nameInferred
                            : 'satellite';

                    elementMapRef.current = {
                        ...elementMapRef.current,
                        [msg.elementId]: {
                            elementId: msg.elementId,
                            elementName: msg.elementName,
                            elementType: autoType,
                            nodeId: payload.C2__source_id,
                        },
                    };
                    setElementMap(elementMapRef.current);
                }

                // Build nodeId → elementId mapping
                if (payload.C2__source_id && !nodeMapRef.current[payload.C2__source_id]) {
                    const newNodeMap = { ...nodeMapRef.current, [payload.C2__source_id]: msg.elementId };
                    nodeMapRef.current = newNodeMap;
                    setNodeIdToElementId(newNodeMap);
                }

                // Store ECEF history in module-level cache (no React re-render)
                const ecefState: ECEFState = {
                    pos_x: payload.RepElementFixedState__pos_x,
                    pos_y: payload.RepElementFixedState__pos_y,
                    pos_z: payload.RepElementFixedState__pos_z,
                    vel_x: payload.RepElementFixedState__vel_x,
                    vel_y: payload.RepElementFixedState__vel_y,
                    vel_z: payload.RepElementFixedState__vel_z,
                    q_1: payload.RepElementFixedState__q_1,
                    q_2: payload.RepElementFixedState__q_2,
                    q_3: payload.RepElementFixedState__q_3,
                    q_4: payload.RepElementFixedState__q_4,
                    timestamp: msg.timestamp,
                };
                appendEcefHistory(msg.elementId, ecefState);

                // Plot/update entity (trails handled internally via module cache)
                handleRepElementFixedState(
                    viewer, msg, elementMapRef.current,
                    visSettingsRef.current,
                );

                // Update selected element info (only if this IS the selected element)
                const posData = parsePositionFromPayload(msg);
                if (posData) {
                    setSelectedElement(prev => {
                        if (!prev || prev.elementId !== msg.elementId) return prev;
                        const euler = computeLVLHEulerAngles(
                            posData.position, posData.velocity, posData.quaternion,
                        );
                        return {
                            ...prev,
                            altitude: computeAltitudeKm(posData.position),
                            speed: computeSpeedMs(posData.velocity),
                            position: posData.position,
                            velocity: posData.velocity,
                            quaternion: posData.quaternion,
                            ...(euler ? { eulerAngles: euler } : {}),
                        };
                    });
                }
            }

            if (msg.commandName === 'RepGetLinkStats') {
                if (!msg.payload) return;
                const linkData = parseLinkStatsFromPayload(msg);
                if (linkData) {
                    setLinkStatsMap(prev => ({
                        ...prev,
                        [msg.elementId]: linkData,
                    }));
                }
            }
        } catch {
            // Skip non-JSON messages (e.g. auth responses)
        }
    }, [viewer, setElementMap, setNodeIdToElementId, setLinkStatsMap, setSelectedElement]);

    // ─── Step 3: Attach/detach WS listener ──────────────────────────
    useEffect(() => {
        if (!socket || !viewer) return;

        socket.addEventListener('message', handleWSMessage);

        return () => {
            socket.removeEventListener('message', handleWSMessage);
        };
    }, [socket, viewer, handleWSMessage]);

    // ─── Step 4: Update comm lines when linkStats or positions change ─
    useEffect(() => {
        if (!isViewerReady(viewer)) return;

        updatePeerCommLines(
            viewer, linkStatsMap, elementMap,
            nodeIdToElementId, visualizationSettings,
        );
        // Geometric comm lines (FOV-based sat↔gs + sat↔sat line-of-sight)
        updateGeometricCommLines(viewer, elementMap, visualizationSettings);

        throttledRequestRender(viewer);
    }, [linkStatsMap, viewer, elementMap, nodeIdToElementId, visualizationSettings]);

    // ─── Step 5: React to visibility settings changes ───────────────
    useEffect(() => {
        if (!isViewerReady(viewer)) return;

        // Toggle orbit tracks
        viewer.entities.values.forEach(e => {
            if (e.id.endsWith('-orbit')) {
                const elId = e.id.replace('-orbit', '');
                e.show = isFeatureVisible(visualizationSettings.orbitTracks, elId)
                    && isFeatureVisible(visualizationSettings.elementVisibility, elId);
            }
            if (e.id.endsWith('-fov')) {
                const elId = e.id.replace('-fov', '');
                e.show = isFeatureVisible(visualizationSettings.fovCones, elId);
            }
            if (e.id.endsWith('-for')) {
                const elId = e.id.replace('-for', '');
                e.show = isFeatureVisible(visualizationSettings.forCones, elId);
            }
            if (e.id.endsWith('-trail')) {
                const elId = e.id.replace('-trail', '');
                e.show = isFeatureVisible(visualizationSettings.trajectoryTrails, elId);
            }
            if (e.id.includes('-axis-')) {
                const elId = e.id.replace(/-axis-[xyz]$/, '');
                e.show = isFeatureVisible(visualizationSettings.orientationAxes, elId);
            }
            if (e.id.startsWith('peer-comm-') || e.id.startsWith('comm-')) {
                // Comm lines connect two elements — extract both IDs from the entity ID
                // Format: "comm-{elIdA}-{elIdB}" or "peer-comm-{elIdA}-{elIdB}"
                const prefix = e.id.startsWith('peer-comm-') ? 'peer-comm-' : 'comm-';
                const idBody = e.id.slice(prefix.length);
                let resolved = false;
                const elIds = Object.keys(elementMapRef.current);
                for (const elId of elIds) {
                    if (idBody.startsWith(elId + '-')) {
                        const candidate = idBody.slice(elId.length + 1);
                        if (elementMapRef.current[candidate]) {
                            e.show = isFeatureVisible(visualizationSettings.commLines, elId)
                                  && isFeatureVisible(visualizationSettings.commLines, candidate);
                            resolved = true;
                            break;
                        }
                    }
                }
                if (!resolved) {
                    e.show = visualizationSettings.commLines.globalEnabled;
                }
            }
        });

        throttledRequestRender(viewer);
    }, [visualizationSettings, viewer]);

    // ─── Step 6: Cleanup on unmount ─────────────────────────────────
    useEffect(() => {
        return () => {
            cleanupCesiumPlot(viewer);
        };
    }, [viewer]);

    /**
     * Manually process a message — useful for replay/summary mode.
     */
    const processMessage = useCallback((msg: WSMessage) => {
        if (!isViewerReady(viewer)) return;
        if (msg.commandName === 'RepElementFixedState') {
            handleRepElementFixedState(
                viewer, msg, elementMapRef.current,
                visSettingsRef.current,
            );
        }
    }, [viewer]);

    return { processMessage };
};

export default useCesiumPlot;
